import { z } from 'zod';
import { Ulid } from './common.js';
/**
 * What an account reports about one of its Boards (plan §8).
 *
 * This file held the install report too. An install is recorded by the Worker now, from the desktop
 * app's job reports (`ReportBoardJobRequest` in `boards.ts`, `docs/off-board-plan.md` §4), and what
 * is on a Board from its syncs — so the request BoardFlight's own app on the Board used to send,
 * and the routes that took it, are gone with that app. What is left is the piece inventory, and the
 * way a report says which Board it is about.
 */
/**
 * Which Board a report is about, as the retired Android client named one.
 *
 * Still parsed wherever a body may carry it — a piece report and a game-page view among them —
 * and never required: the storefront names a Board by its id, or not at all.
 *
 * **`nullish`, not `optional`.** That client serialized with kotlinx's `encodeDefaults = true`, so
 * a field it had nothing for arrived as an explicit `null` rather than a missing key — and
 * `.optional()` accepts a missing key but refuses an explicit null. Reports were once lost to
 * exactly that, in silence, and tolerating both shapes costs nothing.
 *
 * **No name.** A Board's own name is its owner's Board username — `"honzababarik's Board"` — so
 * carrying it put a tester's handle, often their real name, in front of every studio whose game
 * they installed. Nobody needed it to tell one tester's Boards apart, so it is gone rather than
 * gated. A body that still sends `name` has the key stripped on parse (`z.object`), so it is
 * dropped at the door rather than refused, and nothing downstream ever sees it.
 */
export const DeviceIdentity = z.object({
    /**
     * A hash of the Board's `/board/info` identity fields, used only to recognise the same
     * device across reports. The Board's bearer token is never sent here (plan §12).
     */
    boardInfoHash: z.string().trim().max(128).nullish(),
});
/**
 * `POST /v1/events/pieces` — telling BoardFlight which boxes are in the room.
 *
 * A **report**: a fact about a Board, and losing one costs nothing that matters. The inventory is
 * owned where it is edited — on the library's page, per Board; this is the mirror
 * (`docs/model-intro-plan.md` P1), and there is deliberately no route that reads it back — a page
 * that could be told what a room owns would be a page that could disagree with the room.
 *
 * Only a Board whose question has been **answered** is reported. There is no way to say
 * "unconfigured" on this wire, and none is needed: never-answered is the absence of a report,
 * which is also the absence of a row, which is exactly what the server should record about a
 * question nobody answered. Answering *none* is a real report with an empty list, and the two are
 * different everywhere.
 *
 * The list replaces whatever was reported for that Board before rather than merging into it —
 * somebody who unticks a box has to be able to untick it.
 */
export const ReportPiecesRequest = z.object({
    /**
     * Which Board, as the retired Android client named one. `nullish` since `boardId` arrived: a
     * report names its Board one way or the other, and the route refuses one that does neither.
     */
    device: DeviceIdentity.nullish(),
    /**
     * Which Board, as the account knows it (`GET /v1/me/boards`) — how the storefront reports, now
     * that the inventory is edited on a page rather than on the Board (`docs/off-board-plan.md` §6).
     * It must be one of the caller's own Boards. Wins over `device` when both are sent.
     */
    boardId: Ulid.nullish(),
    /**
     * Piece **set** keys — the boxes, not the models. Owning `thrasos` and `arcade` is what
     * satisfies the combined `thrasos_arcade` model, which is why `PieceModel.sets` exists.
     *
     * Lenient about the vocabulary for the same reason `CreateBuildRequest.pieceModel` is: a client
     * newer than this deployment may name a set that shipped in a later release, and a report
     * refused over a name is a report lost for nothing. Deduped on arrival rather than rejected —
     * a repeated key is a slip, and the only thing it could possibly mean is the set once.
     */
    sets: z
        .array(z.string().trim().toLowerCase().regex(/^[a-z0-9_]{1,80}$/))
        .max(64)
        .transform((list) => [...new Set(list)]),
    /** When the answer was given, on the reporter's clock. The server clamps it; that clock is its own. */
    answeredAt: z.number().int().nullish(),
});
//# sourceMappingURL=events.js.map